import createAxios from "@/lib/axios";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  createFileRoute,
  Link,
  Outlet,
  useMatchRoute,
} from "@tanstack/react-router";
import { CalendarDaysIcon, PlusIcon } from "lucide-react";
import { useCallback } from "react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

function formatDate(date: Date) {
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return day + "th";
    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  return `${getOrdinalSuffix(day)} ${month}`;
}

function parseCustomDate(dateString?: string): Date {
  if (!dateString) return new Date();
  const formattedIsoString = dateString.replace("IST+0530", "+05:30");
  const date = new Date(formattedIsoString);
  return isNaN(date.getTime()) ? new Date() : date;
}

export const Route = createFileRoute("/dashboard/material")({
  component: CourseMaterialSidebar,
  loader: async () => {
    try {
      const client = await createAxios();
      const { data } = await client.get<
        { id: string; name?: string; createdAt: string }[]
      >(`${import.meta.env.VITE_BACKEND_URL}/presentations`);
      return { courses: data || [] };
    } catch (error) {
      toast.error("Error fetching presentations:");
      throw error;
    }
  },
});

function CourseMaterialSidebar() {
  const { courses } = Route.useLoaderData();
  const matchRoute = useMatchRoute();
  const isMatched = useCallback(
    (id: string) => {
      return matchRoute({
        to: "/dashboard/material/$id",
        params: { id },
      });
    },
    [matchRoute],
  );
  return (
    <main className="flex w-full">
      <div className="w-full p-4">
        <Outlet />
      </div>
      <nav className="flex h-screen w-[20vw] shrink-0 flex-col items-start gap-4 bg-[#2b3b54] p-4">
        <Link to="/dashboard/material/new/select-subject" className="w-full">
          <Card className="w-full">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center justify-between gap-2 text-base">
                <span>Create</span>
                <PlusIcon className="size-6" />
              </CardTitle>
            </CardHeader>
          </Card>
        </Link>
        <Separator className="-my-1 bg-muted-foreground" />
        <ScrollArea className="w-full">
          <div className="flex w-full flex-col gap-3">
            {courses.map((course) => (
              <Link
                key={course.id}
                to={"/dashboard/material/$id"}
                params={{ id: course.id }}
                className="w-full"
              >
                <Card
                  className={`w-full ${isMatched(course.id) ? "bg-[#e8e9eb]" : "bg-muted-foreground"}`}
                >
                  <CardHeader className="p-4">
                    <h1
                      className="font-semibold"
                      title={course.name || course.id}
                    >
                      {course.name
                        ? course.name.length > 20
                          ? course.name.slice(0, 20) + "..."
                          : course.name
                        : course.id.slice(-10)}
                    </h1>
                    <div className="flex items-center gap-1">
                      <CalendarDaysIcon className="size-4" />
                      <p className="text-sm">
                        {formatDate(parseCustomDate(course.createdAt))}
                      </p>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </ScrollArea>
      </nav>
    </main>
  );
}
